import * as React from 'react';
import './style.css';

import ModalDialog from '../ModalDialog';

type SelectorDialog<T extends SelectorDialog.TypeBase> = (
  ReturnType<SelectorDialog.SFC<T>>
);

function SelectorDialog<T extends SelectorDialog.TypeBase>({
  title,
  options,
  enableNew,
  dialogState,
  OptionName,
  selectionValid,
  onSelection,
  dispatch,
  hideDialog,
  isHidden,
}: SelectorDialog.Props<T>): SelectorDialog<T> {
  const smartSelection = (() => {
    const { selection } = dialogState.data;

    if (!dialogState.data.newOptionText) {
      return selection;
    }

    if (selection.type === 'new') {
      if (!enableNew) {
        return { type: 'none' as 'none' };
      }

      for (const option of options) {
        if (
          dialogState.data.newOptionText.toLowerCase() ===
          OptionName(option).toLowerCase()
        ) {
          return { type: 'existing' as 'existing', data: option };
        }
      }
    }

    return selection;
  })();

  const existingSelectedOption = (
    smartSelection.type === 'existing' ? smartSelection.data : null
  );

  const dispatchSelection = (
    selectionValid(smartSelection) ?
    (() => {
      onSelection(smartSelection);
      hideDialog();
    }) :
    null
  );

  return (
    <ModalDialog
      className="selector-dialog"
      title={title}
      buttons={[
        ['Cancel', hideDialog],
        [
          (
            smartSelection.type === 'none' ? 'Clear' :
            smartSelection.type === 'new' ? 'Add' :
            'Select'
          ),
          dispatchSelection,
        ],
      ]}
      bodyPadding="0"
      hidden={isHidden()}
      hideDialog={hideDialog}
    >
      {!enableNew ? [] : (
        <div
          className={(
            `option${smartSelection.type === 'new' ? ' selected' : ''}`
          )}
          style={{ padding: '0' }}
        >
          <input
            type="text"
            placeholder="New"
            value={dialogState.data.newOptionText}
            id="new-option-textbox"
            autoFocus={smartSelection.type === 'new'}
            onInput={(e: React.FormEvent<HTMLInputElement>) => {
              dispatch({ ...dialogState,
                data: { ...dialogState.data,
                  newOptionText: (e.target as HTMLInputElement).value,
                }
              });
            }}
            onKeyDown={e => {
              if (dispatchSelection && e.key === 'Enter') {
                dispatchSelection();
              }
            }}
            onClick={() => {
              dispatch({ ...dialogState,
                data: { ...dialogState.data,
                  selection: { type: 'new' },
                }
              });
            }}
          />
        </div>
      )}
      {options.map(opt => {
        if (opt === existingSelectedOption) {
          return (
            <div
              className="option selected"
              onClick={() => {
                dispatch({ ...dialogState,
                  data: { ...dialogState.data,
                    selection: { type: 'none' },
                  },
                });
              }}
            >
              {OptionName(opt)}
            </div>
          );
        }

        return (
          <div
            key={OptionName(opt)}
            className="option"
            onClick={() => {
              dispatch({ ...dialogState,
                data: { ...dialogState.data,
                  selection: {
                    type: 'existing',
                    data: opt,
                  }
                },
              });
            }}
          >
            {OptionName(opt)}
          </div>
        );
      })}
    </ModalDialog>
  );
}

namespace SelectorDialog {
  export type TypeBase = {
    type: string,
    option: {},
    metadata: {},
  };

  export type State<T extends TypeBase> = {
    type: T['type'],
    data: {
      selection: (
        { type: 'new' } |
        { type: 'none' } |
        { type: 'existing', data: T['option'] }
      ),
      newOptionText: string,
    },
    metadata: T['metadata'],
  };

  export type Props<T extends TypeBase> = {
    title: string,
    options: T['option'][],
    enableNew: boolean,
    dialogState: SelectorDialog.State<T>,
    OptionName: (option: T['option']) => string,
    selectionValid: (
      selection: SelectorDialog.State<T>['data']['selection']
    ) => boolean,
    onSelection: (
      selection: SelectorDialog.State<T>['data']['selection']
    ) => void,
    dispatch: (newDialog: SelectorDialog.State<T>) => void,
    hideDialog: () => void,
    isHidden: () => boolean,
  };

  export type SFC<T extends TypeBase> = React.SFC<Props<T>>;
}

export default SelectorDialog;



// WEBPACK FOOTER //
// ./src/ui/SelectorDialog/index.tsx